import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '../../notifications';
import {
  getWhatsAppAppGroup,
  getWhatsAppAppGroupInvite,
  leaveWhatsAppAppGroup,
  renameWhatsAppAppGroup,
  revokeWhatsAppAppGroupInvite,
  sendWhatsAppAppGroupInvite,
  updateWhatsAppAppGroupParticipants,
  WhatsAppAppGroup,
  WhatsAppAppGroupInvite,
} from '../services/whatsAppAppGroupService';
import detailsStyles from './ConversationDetails.module.css';

interface WhatsAppAppGroupPanelProps {
  conversationId: string;
  title?: string;
  pictureUrl?: string;
  onTitleChanged?: (title: string) => void;
  onLeft?: () => void;
}

const WhatsAppAppGroupPanel: React.FC<WhatsAppAppGroupPanelProps> = ({
  conversationId,
  title,
  pictureUrl,
  onTitleChanged,
  onLeft,
}) => {
  const { t } = useTranslation('conversations');
  const { showToast } = useToast();
  const [group, setGroup] = useState<WhatsAppAppGroup | null>(null);
  const [invite, setInvite] = useState<WhatsAppAppGroupInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState(title ?? '');
  const [memberNumber, setMemberNumber] = useState('');
  const [inviteNumber, setInviteNumber] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextGroup, nextInvite] = await Promise.all([
        getWhatsAppAppGroup(conversationId),
        getWhatsAppAppGroupInvite(conversationId).catch(() => null),
      ]);
      setGroup(nextGroup);
      setInvite(nextInvite);
      setSubject(nextGroup.title ?? title ?? '');
    } catch {
      showToast(t('failedToLoadWhatsAppGroup', { defaultValue: 'Failed to load WhatsApp group.' }), 'error');
    } finally {
      setLoading(false);
    }
  }, [conversationId, showToast, t, title]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(async (work: () => Promise<void>, successKey: string, fallback: string) => {
    if (busy) {
      return;
    }

    setBusy(true);
    try {
      await work();
      showToast(t(successKey, { defaultValue: fallback }), 'success');
      await load();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('whatsAppGroupActionFailed', { defaultValue: 'WhatsApp group action failed.' }), 'error');
    } finally {
      setBusy(false);
    }
  }, [busy, load, showToast, t]);

  if (loading && !group) {
    return <div className={detailsStyles.groupPanel}>{t('loading', { ns: 'common' })}</div>;
  }

  return (
    <div className={detailsStyles.groupPanel}>
      <div className={detailsStyles.groupPanelHeader}>
        {pictureUrl || group?.pictureUrl ? (
          <img src={pictureUrl || group?.pictureUrl} alt="" className={detailsStyles.groupPanelPicture} />
        ) : null}
        <div>
          <div className={detailsStyles.groupPanelTitle}>{group?.title || title || t('whatsAppGroup', { defaultValue: 'WhatsApp group' })}</div>
          <div className={detailsStyles.groupPanelMeta}>{t('whatsAppGroupMembersCount', { count: group?.members.length ?? 0, defaultValue: '{{count}} members' })}</div>
        </div>
      </div>

      <div className={detailsStyles.groupPanelRow}>
        <input
          className={detailsStyles.groupPanelInput}
          value={subject}
          onChange={event => setSubject(event.target.value)}
          placeholder={t('whatsAppGroupName', { defaultValue: 'Group name' })}
        />
        <button
          type="button"
          className={detailsStyles.groupPanelButton}
          disabled={busy || !subject.trim()}
          onClick={() => void run(async () => {
            await renameWhatsAppAppGroup(conversationId, subject.trim());
            onTitleChanged?.(subject.trim());
          }, 'whatsAppGroupRenamed', 'Group renamed')}
        >
          {t('rename', { defaultValue: 'Rename' })}
        </button>
      </div>

      <div className={detailsStyles.groupMembers}>
        {(group?.members ?? []).map(member => (
          <div key={member.jid} className={detailsStyles.groupMemberRow}>
            <span>
              {member.name || member.phoneNumber || member.jid}
              {member.admin ? ` · ${member.admin}` : ''}
            </span>
            <button
              type="button"
              className={detailsStyles.groupPanelGhostButton}
              disabled={busy}
              onClick={() => void run(
                () => updateWhatsAppAppGroupParticipants(
                  conversationId,
                  isWhatsAppGroupAdmin(member.admin) ? 'demote' : 'promote',
                  [member.phoneNumber || member.jid],
                ),
                isWhatsAppGroupAdmin(member.admin) ? 'whatsAppGroupMemberDemoted' : 'whatsAppGroupMemberPromoted',
                isWhatsAppGroupAdmin(member.admin) ? 'Admin removed' : 'Member promoted',
              )}
            >
              {isWhatsAppGroupAdmin(member.admin)
                ? t('demote', { defaultValue: 'Demote' })
                : t('promote', { defaultValue: 'Promote' })}
            </button>
            <button
              type="button"
              className={detailsStyles.groupPanelGhostButton}
              disabled={busy}
              onClick={() => void run(
                () => updateWhatsAppAppGroupParticipants(conversationId, 'remove', [member.phoneNumber || member.jid]),
                'whatsAppGroupMemberRemoved',
                'Member removed',
              )}
            >
              {t('remove', { defaultValue: 'Remove' })}
            </button>
          </div>
        ))}
      </div>

      <div className={detailsStyles.groupPanelRow}>
        <input
          className={detailsStyles.groupPanelInput}
          value={memberNumber}
          onChange={event => setMemberNumber(event.target.value)}
          placeholder={t('whatsAppGroupAddPlaceholder', { defaultValue: 'Add number' })}
        />
        <button
          type="button"
          className={detailsStyles.groupPanelButton}
          disabled={busy || !memberNumber.trim()}
          onClick={() => void run(async () => {
            await updateWhatsAppAppGroupParticipants(conversationId, 'add', [memberNumber.trim()]);
            setMemberNumber('');
          }, 'whatsAppGroupMemberAdded', 'Member added')}
        >
          {t('add', { defaultValue: 'Add' })}
        </button>
      </div>

      <div className={detailsStyles.groupInvite}>
        <div className={detailsStyles.groupPanelMeta}>
          {invite?.inviteUrl || invite?.inviteCode || t('whatsAppGroupInviteUnavailable', { defaultValue: 'No invite link yet' })}
        </div>
        <div className={detailsStyles.groupPanelRow}>
          <button
            type="button"
            className={detailsStyles.groupPanelGhostButton}
            disabled={busy || !(invite?.inviteUrl || invite?.inviteCode)}
            onClick={() => {
              const value = invite?.inviteUrl || invite?.inviteCode;
              if (value) {
                void navigator.clipboard.writeText(value);
                showToast(t('copied', { defaultValue: 'Copied' }), 'success');
              }
            }}
          >
            {t('copyInvite', { defaultValue: 'Copy invite' })}
          </button>
          <button
            type="button"
            className={detailsStyles.groupPanelGhostButton}
            disabled={busy}
            onClick={() => void run(() => revokeWhatsAppAppGroupInvite(conversationId), 'whatsAppGroupInviteRevoked', 'Invite revoked')}
          >
            {t('revokeInvite', { defaultValue: 'Revoke' })}
          </button>
        </div>
        <div className={detailsStyles.groupPanelRow}>
          <input
            className={detailsStyles.groupPanelInput}
            value={inviteNumber}
            onChange={event => setInviteNumber(event.target.value)}
            placeholder={t('whatsAppGroupInvitePlaceholder', { defaultValue: 'Send invite to number' })}
          />
          <button
            type="button"
            className={detailsStyles.groupPanelButton}
            disabled={busy || !inviteNumber.trim()}
            onClick={() => void run(async () => {
              await sendWhatsAppAppGroupInvite(conversationId, [inviteNumber.trim()]);
              setInviteNumber('');
            }, 'whatsAppGroupInviteSent', 'Invite sent')}
          >
            {t('sendInvite', { defaultValue: 'Send invite' })}
          </button>
        </div>
      </div>

      <button
        type="button"
        className={detailsStyles.groupPanelDangerButton}
        disabled={busy}
        onClick={() => {
          if (!window.confirm(t('leaveWhatsAppGroupConfirm', { defaultValue: 'Leave this WhatsApp group?' }))) {
            return;
          }

          void run(async () => {
            await leaveWhatsAppAppGroup(conversationId);
            onLeft?.();
          }, 'leftWhatsAppGroup', 'Left group');
        }}
      >
        {t('leaveWhatsAppGroup', { defaultValue: 'Leave group' })}
      </button>
    </div>
  );
};

const isWhatsAppGroupAdmin = (admin?: string): boolean => {
  const value = admin?.trim().toLowerCase();
  return Boolean(value) && value !== 'false' && value !== '0';
};

export default WhatsAppAppGroupPanel;
